// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import { getOutputAttributeDataTypes } from 'flow/service/actions';
import type { DataType, DataTypeOption, FormState } from './types';
import type { NodeEditorFormAdapter } from '../../useNodeEditorForm';
import { getOutputValueRangeOptions, mergeFormData } from '../../formHelpers';
import { parseBooleanString } from 'flow/service/util';
import { buildIndexableActionOptionEntry } from 'flow/store/engine/create/actions/helpers';
import { OUTPUT_CATEGORY_RISK_SCORING } from 'flow/store/engine/constants';
import type { OutputValueRange } from 'flow/store/engine/types';

export function getDefineValueRangesHelpText({
  outputCategory,
  outputValueRange,
  isCraConfigPasEnabled,
}: {
  outputCategory?: string;
  outputValueRange?: OutputValueRange[];
  isCraConfigPasEnabled: boolean;
}): string | undefined {
  if (outputCategory && !isCraConfigPasEnabled) {
    return 'You must define ranges for values if the Risk Scoring category is selected.';
  }
  if (isCraConfigPasEnabled && (!outputValueRange || outputValueRange.length === 0)) {
    return 'Ranges for values can only be set in Published Attribute settings.';
  }
  return undefined;
}

export function getDataTypeOptions(): DataTypeOption[] {
  const dataTypes = getOutputAttributeDataTypes();
  return dataTypes.map(buildDataTypeOption);
}

export function buildDataTypeOption(dataType: DataType): DataTypeOption {
  return {
    label: capitalize(dataType),
    value: dataType,
  };
}

export const outputAttributeOptionsFormAdapter: NodeEditorFormAdapter<FormState> = {
  getDefaultFormValues(action, workflowProps) {
    const { options, output } = action;

    const isPublishedAttribute = Boolean(output.publishedAttributeToken);
    const publishedAttribute = workflowProps.publishedAttributes
      .find(pa => pa.attributeToken === output.publishedAttributeToken);
    const outputValueRange = (options?.output_value_range?.jsonValue ?? []) as OutputValueRange[];
    return {
      attributeName: publishedAttribute?.attributeName ?? output.name,
      attributeToken: output.publishedAttributeToken,
      // naming `outputCategory` instead of `category`, it conflicts with the tag category within form state
      ...(isPublishedAttribute && { outputCategory: (options?.category?.value ?? '') as string }),
      containsPii: publishedAttribute?.containsPii,
      dataType: (publishedAttribute?.dataType || output.dataType)
        ? buildDataTypeOption(publishedAttribute?.dataType ?? output.dataType as DataType)
        : null,
      defaultValue: publishedAttribute?.defaultValue,
      defaultValueNote: publishedAttribute?.defaultValueNote,
      hasExpiration: publishedAttribute?.hasExpiration,
      hasJqFilter: isPublishedAttribute
        ? publishedAttribute?.hasJqFilter
        : parseBooleanString(output.useJq),
      // output Action to form -> convert from boolean string to pure boolean
      jqFilter: isPublishedAttribute ? publishedAttribute?.jqFilter : output.jqFunction,
      valueExpiresInDays: publishedAttribute?.valueExpiresInDays,
      validValueMin: publishedAttribute?.validValueMin,
      validValueMax: publishedAttribute?.validValueMax,
      validValues: publishedAttribute?.validValues,
      agentCanCreateValue: publishedAttribute?.agentCanCreateValue,
      groupingToken: publishedAttribute?.groupingToken,
      // operate1-3888 TODO: defineValueRanges should also default true if value ranges are defined
      ...(isPublishedAttribute && {
        defineValueRanges: Boolean(
          options?.category?.value === OUTPUT_CATEGORY_RISK_SCORING
          || (options?.output_value_range?.jsonValue as OutputValueRange[])?.length > 0,
        ),
        outputValueRange,
        outputValueRangeOptions: getOutputValueRangeOptions({
          outputValueRange,
          outputValueRangeOptions: output.outputValueRangeOptions,
        }),
      }),
      // not supported yet:
      // description: output.description,
    };
  },
  // shared util for autoSave + save functions therefore -
  // this maps form data **specific to the output action + its action options**
  // published attribute form data is mapped in the reducer (useTyphonEndpoints) or updated via API request
  mapFormDataToAction(data, sourceAction) {
    // handle updates to output action
    const prevState = {
      options: {
        ...sourceAction.options,
        // Action option lgic for category and output_value_range:
        // - With `cra-config-pas` flag ON:
        //   Category/range config comes from the PA (sourceAction)
        //   and will already exist in sourceAction.options (action options will already exist)
        // - With `cra-config-pas` flag OFF:
        //   If category/range config don't exist in sourceAction.options,
        //   they may be set in the form data (data.outputCategory, data.outputValueRange)
        //   and we need to create new action options for them
        ...(!sourceAction.options.category && data.outputCategory
          ? buildIndexableActionOptionEntry('category', { value: data.outputCategory })
          : {}),
        ...(!sourceAction.options.output_value_range
            && Array.isArray(data.outputValueRange) && data.outputValueRange.length > 0
          ? buildIndexableActionOptionEntry('output_value_range', {
            value: data.outputValueRange as OutputValueRange[],
            jsonValue: data.outputValueRange as OutputValueRange[],
          })
          : {}),
      },
      output: {
        dataType: sourceAction.output.dataType,
        publishedAttributeToken: sourceAction.output.publishedAttributeToken,
        jqFilter: sourceAction.output.jqFunction,
        name: sourceAction.output.name,
        hasJqFilter: Boolean(sourceAction.output.useJq),
        outputValueRangeOptions: sourceAction.output.outputValueRangeOptions,
      },
    };

    const afterState = {
      options: {
        ...(prevState.options.category && {
          category: {
            ...prevState.options.category,
            value: data.outputCategory,
          },
          output_value_range: data.defineValueRanges ? {
            ...prevState.options.output_value_range,
            value: data.outputValueRange,
            jsonValue: data.outputValueRange,
          } : {
            ...prevState.options.output_value_range,
            value: [] as OutputValueRange[],
            jsonValue: [] as OutputValueRange[],
          },
        }),
      },
      output: {
        dataType: data.dataType.value,
        publishedAttributeToken: data.attributeToken,
        jqFilter: data.jqFilter,
        name: data.attributeName,
        hasJqFilter: data.hasJqFilter,
        outputValueRangeOptions: data.defineValueRanges ? data.outputValueRangeOptions : null,
      },
    };

    const mergedData = mergeFormData(prevState, afterState);
    const formattedData = {
      ...mergedData,
      output: {
        ...mergedData.output,
        hasJqFilter: String(mergedData.output.hasJqFilter),
      },
    };

    return {
      ...sourceAction,
      options: {
        ...sourceAction.options,
        ...formattedData.options,
      },
      output: {
        ...sourceAction.output,
        ...formattedData.output,
        ...(typeof data.defineValueRanges === 'boolean' && {
          defineValueRanges: data.defineValueRanges,
        }),
        // not supported yet:
        // description: data.description,
      },
    };
  },
};
